import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Banner,
  BlockStack,
  InlineGrid,
  DataTable,
  SkeletonBodyText,
} from '@shopify/polaris';
import { useSubscription } from '../hooks/useSubscription';
import { useUsage } from '../hooks/useUsage';
import { usePlans } from '../hooks/usePlans';
import { useLanguage } from '../providers/LanguageProvider';
import UsageProgressBar from '../components/UsageProgressBar';
import TrialQuotaBanner from '../components/TrialQuotaBanner';
import type { PageName } from '../types';
import { PlanTier } from '../types';

interface UsagePageProps {
  onNavigate: (page: PageName) => void;
}

/** Prices are quoted in USD on every locale */
function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export default function UsagePage({ onNavigate }: UsagePageProps) {
  const { subscription, loading: subLoading } = useSubscription();
  const { usage, loading: usageLoading } = useUsage();
  const { plans, loading: plansLoading } = usePlans();
  const { t, language } = useLanguage();

  const loading = subLoading || usageLoading || plansLoading;
  const dateLocale = language === 'it' ? 'it-IT' : 'en-US';

  // No subscription state
  if (!loading && !subscription) {
    return (
      <Page title={t.usage.title}>
        <Layout>
          <Layout.Section>
            <Banner
              title={t.usage.noSubscriptionTitle}
              tone="info"
              action={{
                content: t.usage.viewPlans,
                onAction: () => onNavigate('subscription'),
              }}
            >
              <p>{t.usage.noSubscriptionBody}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Page title={t.usage.title}>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={5} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!usage) {
    return (
      <Page title={t.usage.title}>
        <Layout>
          <Layout.Section>
            <Banner tone="warning">
              <p>{t.usage.noUsageData}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Find current plan details
  const currentPlan = plans?.find((p) => p.id === usage.planTier);

  const isFreePlan = usage.planTier === PlanTier.FREE;
  const quotaUsedUp = usage.remainingTryOns <= 0;
  // Overage keeps the store serving past the included quota, so an exhausted
  // quota is news, not an outage.
  const overageCovering = usage.overageEnabled && quotaUsedUp;

  // Usage breakdown data. The trial and overage rows only appear when they apply,
  // so a merchant on a plain paid plan sees exactly what they saw before.
  const usageRows = [
    [
      usage.isTrial && usage.trialCap !== null ? t.usage.trialTitle : t.usage.includedTryOns,
      usage.includedTryOns.toString(),
    ],
    [t.usage.usedTryOns, usage.usedTryOns.toString()],
    [t.usage.remainingTryOns, usage.remainingTryOns.toString()],
    ...(usage.overageEnabled
      ? [
          [t.usage.overageTryOns, usage.overageTryOns.toString()],
          [t.usage.overageSpend, formatUsd(usage.overageSpend)],
          [t.usage.overageCap, formatUsd(usage.overageCappedAmount ?? 0)],
        ]
      : []),
  ];

  const usagePercent =
    usage.includedTryOns > 0 ? (usage.usedTryOns / usage.includedTryOns) * 100 : 0;

  return (
    <Page title={t.usage.title}>
      <Layout>
        {/* Overage is covering the store, so nothing is blocked */}
        {overageCovering && usage.overagePricePerTryOn !== null && (
          <Layout.Section>
            <Banner tone="info" title={t.usage.overageTitle}>
              <p>{t.usage.overageActiveBody(formatUsd(usage.overagePricePerTryOn))}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Trial running low, or spent, with the way out of it */}
        {!overageCovering && <TrialQuotaBanner usage={usage} />}

        {/* Quota exhausted upgrade prompt */}
        {quotaUsedUp && !overageCovering && !usage.isTrial && (
          <Layout.Section>
            <Banner
              tone="critical"
              title={t.usage.quotaExhausted}
              action={{ content: t.usage.upgradeNow, onAction: () => onNavigate('subscription') }}
            >
              <p>{t.usage.quotaExhaustedBody}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Free plan explainer, while there are still try-ons left */}
        {!quotaUsedUp && isFreePlan && (
          <Layout.Section>
            <Banner
              tone="info"
              title={t.usage.freePlanTitle}
              action={{ content: t.usage.upgradeNow, onAction: () => onNavigate('subscription') }}
            >
              <p>{t.usage.freePlanBody(usage.includedTryOns)}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Approaching limit warning */}
        {!quotaUsedUp && !isFreePlan && usagePercent > 80 && (
          <Layout.Section>
            <Banner
              tone="warning"
              action={{ content: t.usage.upgradeNow, onAction: () => onNavigate('subscription') }}
            >
              <p>{t.usage.approachingLimit(usage.remainingTryOns)}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Usage Progress Visualization */}
        <Layout.Section>
          <Card>
            <UsageProgressBar
              includedTryOns={usage.includedTryOns}
              usedTryOns={usage.usedTryOns}
              remainingTryOns={usage.remainingTryOns}
            />
          </Card>
        </Layout.Section>

        {/* Usage Breakdown */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.usage.usageBreakdown}
              </Text>
              <DataTable
                columnContentTypes={['text', 'numeric']}
                headings={[t.usage.metric, t.usage.count]}
                rows={usageRows}
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Billing Period Info */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.usage.billingPeriod}
              </Text>
              <InlineGrid columns={3} gap="400">
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t.usage.startDate}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    {new Date(usage.billingPeriodStart).toLocaleDateString(dateLocale)}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t.usage.endDate}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    {new Date(usage.billingPeriodEnd).toLocaleDateString(dateLocale)}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t.usage.daysRemaining}
                  </Text>
                  <Badge tone={usage.daysRemaining < 5 ? 'warning' : 'info'}>
                    {t.usage.days(usage.daysRemaining)}
                  </Badge>
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Plan Details */}
        {currentPlan && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t.usage.currentPlanDetails}
                </Text>
                <InlineGrid columns={2} gap="400">
                  <BlockStack gap="100">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t.usage.plan}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {currentPlan.name}
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t.usage.monthlyPrice}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {`$${currentPlan.price.toFixed(2)}`}
                    </Text>
                  </BlockStack>
                </InlineGrid>
                <Text as="p" variant="bodySm" tone="subdued">
                  {currentPlan.description}
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
